import React, { useEffect } from 'react';
import { Form, Radio, Switch, Input, Select, DatePicker, Row, Col, Divider } from 'antd';
import CBInputNumber from './CBInputNumber';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { localeIntl } from '../i18n/format';
import { TYPES_VICTIME, STATUTS_ACTIVITE } from '../variables/constants';
import SelectWithAdd from './SelectWithAdd';
import NegociationRcPanel from './NegociationRcPanel';
import CBModal from './CBModal';
import ChampResponsabilite, { responsabiliteDepuisTaux, tauxRcDepuisSaisie } from './ChampResponsabilite';
import { fieldLabel, RequiredLegend } from './FieldLabel';
const { Option } = Select;

const GREEN = '#1a6b3c';
const LINE = '#e7e9ee';
const INK_MUTED = '#5a6676';

const lbl = (text, required) => fieldLabel(text, required);

const fmtFcfa = (v) => `${new Intl.NumberFormat(localeIntl()).format(Math.round(v || 0))} FCFA`;

const inputH = {
  style: { height: 40, borderRadius: 8, borderColor: LINE, fontSize: 14 },
};

/**
 * Modal réutilisable d'ajout / édition d'une victime (ou adversaire).
 *
 *   <VictimeModal
 *     open={...}
 *     onClose={...}
 *     onSave={(victime) => ...}     // Reçoit l'objet prêt à envoyer à l'API (dateNaissance YYYY-MM-DD)
 *     initial={null}                // ou l'objet victime pour édition
 *     pays={[...]}                  // référentiel pays (optionnel)
 *     defaultAdversaire={false}     // pré-coche "adversaire" à l'ouverture
 *     loading={false}               // spinner du bouton OK pendant la sauvegarde
 *   />
 */
export default function VictimeModal({
  open, onClose, onSave, onRcUpdated, initial, pays = [], defaultAdversaire = false, loading = false,
}) {
  const { t } = useTranslation('commun');
  const [form] = Form.useForm();
  // Une victime décédée a forcément subi un dommage corporel : lui demander le
  // type de dommage n'a pas de sens, et la réponse ne peut être que celle-là.
  const estDecedee = Form.useWatch('typeVictime', form) === 'DECEDE';

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (initial) {
      form.setFieldsValue({
        ...initial,
        dateNaissance: initial.dateNaissance ? dayjs(initial.dateNaissance) : null,
        estResponsable: responsabiliteDepuisTaux(initial.tauxRc),
      });
    } else {
      form.setFieldsValue({
        typeVictime:  defaultAdversaire ? 'NEUTRE' : 'BLESSE',
        sexe:         'M',
        typeDommage:  'CORPOREL',
        estAdversaire: !!defaultAdversaire,
        langue:       'FR',
      });
    }
  }, [open, initial, defaultAdversaire, form]);

  const submit = async () => {
    try {
      const vals = await form.validateFields();
      const { estResponsable, ...reste } = vals;
      onSave({
        ...reste,
        // Le champ étant masqué pour une victime décédée, on pose la seule valeur
        // possible plutôt que de laisser passer ce que contenait le formulaire avant
        // que la situation ne change.
        typeDommage: vals.typeVictime === 'DECEDE' ? 'CORPOREL' : vals.typeDommage,
        dateNaissance: vals.dateNaissance?.format('YYYY-MM-DD') || null,
        tauxRc: tauxRcDepuisSaisie(vals),
      });
    } catch {
      /* validation inline */
    }
  };

  const isAdversaire = Form.useWatch('estAdversaire', form);

  /* SMIG du pays de residence, a titre indicatif.
     Le champ reste MODIFIABLE a dessein. Le moteur CIMA applique deja lui-meme
     le SMIG quand le revenu est nul (CalculCimaServiceImpl : revenu > 0 ? revenu
     : smigRetenu), et le SMIG qu'il retient est MAX(SMIG pays gestionnaire, SMIG
     pays residence) - pas celui de la residence seule. Prereplir puis verrouiller
     ce champ ecrirait donc une valeur parfois trop basse et, surtout, rendrait
     impossible la saisie d'un revenu reel superieur au SMIG : l'ITT (calculee
     seulement si revenu > SMIG), le prejudice economique, l'art. 263 et le
     prejudice scolaire s'en trouveraient tous sous-evalues. */
  const paysResidenceId = Form.useWatch('paysResidenceTrackingId', form);
  const smigPaysResidence = React.useMemo(() => {
    const p = pays.find((x) => x.paysTrackingId === paysResidenceId);
    const v = p?.smigMensuel;
    return v == null ? null : Number(v);
  }, [pays, paysResidenceId]);

  const aideRevenu = smigPaysResidence != null
    ? t('victimeModal.aideRevenuSmigPays', { smig: fmtFcfa(smigPaysResidence) })
    : t('victimeModal.aideRevenuSmigReference');

  const title = initial
    ? (isAdversaire ? t('victimeModal.titres.modifierAdversaire') : t('victimeModal.titres.modifierVictime'))
    : (defaultAdversaire ? t('victimeModal.titres.ajouterAdversaire') : t('victimeModal.titres.ajouterVictime'));

  return (
    <CBModal
      title={<span style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>{title}</span>}
      open={open}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={loading}
      okText={initial ? t('victimeModal.enregistrer') : t('victimeModal.ajouter')}
      cancelText={t('victimeModal.annuler')}
      width={720}
      okButtonProps={{ style: { background: GREEN, borderColor: GREEN, minWidth: 130, height: 38, borderRadius: 9, fontWeight: 600 } }}
      cancelButtonProps={{ style: { height: 38, borderRadius: 9 } }}
      closeIcon={<span style={{ color: '#fff', fontSize: 17 }}>×</span>}
      destroyOnClose
      styles={{
        content: {
          borderRadius: 12,
          overflow: 'hidden',
          padding: 0,
          border: '1px solid #cbd3e0',
          boxShadow: '0 28px 56px -16px rgba(16,24,40,0.34), 0 8px 20px -12px rgba(16,24,40,0.22)',
        },
        header: {
          background: GREEN,
          borderBottom: 'none',
          borderRadius: '10px 10px 0 0',
          padding: '15px 22px',
          marginBottom: 0,
        },
        body: { padding: '20px 22px 4px' },
      }}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 8 }} requiredMark={false}>
        {/* Situation */}
        <Form.Item
          name="typeVictime"
          label={lbl(t('victimeModal.champs.situation'), true)}
          rules={[{ required: true, message: t('victimeModal.obligatoire') }]}
          style={{ marginBottom: 16 }}
        >
          <Radio.Group style={{ display: 'flex', gap: 8, width: '100%' }}>
            {TYPES_VICTIME
              .filter(t => t.value !== 'NEUTRE' || initial?.typeVictime === 'NEUTRE')
              .map(t => (
              <Radio.Button
                key={t.value}
                value={t.value}
                style={{ flex: 1, textAlign: 'center', borderRadius: 6, height: 38, lineHeight: '36px' }}
              >
                {t.label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>

        {/* Identité */}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="nom"
              label={lbl(t('victimeModal.champs.nom'))}
            >
              <Input {...inputH} placeholder="LAKMON" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="prenoms"
              label={lbl(t('victimeModal.champs.prenoms'))}
            >
              <Input {...inputH} placeholder="Adjikamba" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="sexe"
              label={lbl(t('victimeModal.champs.sexe'))}
            >
              <Select {...inputH}>
                <Option value="M">{t('victimeModal.sexes.M')}</Option>
                <Option value="F">{t('victimeModal.sexes.F')}</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="dateNaissance"
              label={lbl(t('victimeModal.champs.dateNaissance'))}
              extra={<span style={{ fontSize: 11.5, color: INK_MUTED }}>{t('victimeModal.aideDateNaissance')}</span>}
            >
              <DatePicker
                style={{ ...inputH.style, width: '100%' }}
                format="DD/MM/YYYY"
                placeholder={t('victimeModal.formatDate')}
                disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="nationalite" label={lbl(t('victimeModal.champs.nationalite'))}>
              <Input {...inputH} placeholder={t('victimeModal.exempleNationalite')} maxLength={50} />
            </Form.Item>
          </Col>
        </Row>

        {/* Contact : l'offre part par courriel à la victime - sans adresse, l'envoi reste bloqué.
            Sa langue est celle de l'offre, de la note et de la quittance qu'on lui remet ; une
            partie adverse n'en reçoit pas. */}
        <Row gutter={16}>
          <Col xs={24} sm={isAdversaire ? 12 : 8}>
            <Form.Item name="telephone" label={lbl(t('victimeModal.champs.telephone'))}>
              <Input {...inputH} placeholder="+228 90 00 00 00" maxLength={30} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={isAdversaire ? 12 : 8}>
            <Form.Item name="email" label={lbl(t('victimeModal.champs.courriel'))}
              extra={<span style={{ fontSize: 11.5, color: INK_MUTED }}>{t('victimeModal.aideCourriel')}</span>}
              rules={[{ type: 'email', message: t('victimeModal.adresseInvalide') }]}>
              <Input {...inputH} placeholder={t('victimeModal.exempleCourriel')} maxLength={150} />
            </Form.Item>
          </Col>
          {!isAdversaire && (
            <Col xs={24} sm={8}>
              <Form.Item name="langue" label={lbl(t('victimeModal.champs.langue'))}
                extra={<span style={{ fontSize: 11.5, color: INK_MUTED }}>{t('victimeModal.aideLangue')}</span>}>
                <Select {...inputH} options={['FR', 'EN'].map((code) => ({ value: code, label: t(`victimeModal.langues.${code}`) }))} />
              </Form.Item>
            </Col>
          )}
        </Row>

        {/* Dommage + contexte indemnisation */}
        <Row gutter={16}>
          {!estDecedee && (
            <Col xs={24} sm={12}>
              <Form.Item
                name="typeDommage"
                label={lbl(t('victimeModal.champs.typeDommage'))}
              >
                <Select {...inputH} placeholder={t('victimeModal.selectionner')}>
                  <Option value="CORPOREL">{t('victimeModal.typesDommage.CORPOREL')}</Option>
                  <Option value="MATERIEL">{t('victimeModal.typesDommage.MATERIEL')}</Option>
                  {/* MIXTE n'est plus proposé à la saisie : une victime est corporel OU matériel.
                      On ne le réaffiche que pour éditer une victime legacy déjà enregistrée MIXTE,
                      afin de ne pas montrer une valeur orpheline ni la muter silencieusement. */}
                  {initial?.typeDommage === 'MIXTE' && <Option value="MIXTE">{t('victimeModal.typesDommage.MIXTE')}</Option>}
                </Select>
              </Form.Item>
            </Col>
          )}
          <Col xs={24} sm={estDecedee ? 24 : 12}>
            <Form.Item
              name="paysResidenceTrackingId"
              label={lbl(t('victimeModal.champs.paysResidence'))}
              extra={<span style={{ fontSize: 11.5, color: INK_MUTED }}>{t('victimeModal.aidePaysResidence')}</span>}
            >
              <Select
                {...inputH}
                showSearch allowClear
                placeholder={t('victimeModal.selectionner')}
                optionFilterProp="label"
                options={pays.map(p => ({ label: p.libelle, value: p.paysTrackingId }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="statutActivite"
              label={lbl(t('victimeModal.champs.statutActivite'))}
            >
              <Select {...inputH} placeholder={t('victimeModal.selectionner')}>
                {STATUTS_ACTIVITE.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="profession"
              label={lbl(t('victimeModal.champs.profession'))}
            >
              <SelectWithAdd
                categorie="PROFESSION"
                placeholder={t('victimeModal.selectionnerProfession')}
                addLabel={t('victimeModal.ajouterProfession')}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              name="revenuMensuel"
              label={lbl(t('victimeModal.champs.revenuMensuel'))}
              extra={<span style={{ fontSize: 11.5, color: INK_MUTED }}>{aideRevenu}</span>}
            >
              <CBInputNumber
                style={{ ...inputH.style, width: '100%' }}
                min={0} step={5000}
                placeholder={smigPaysResidence != null ? t('victimeModal.placeholderSmig', { smig: fmtFcfa(smigPaysResidence) }) : t('victimeModal.exempleRevenu')}
                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                parser={(v) => v?.replace(/\s/g, '')}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Responsabilité de la victime dans l'accident : sa propre part, pas celle de l'assuré. */}
        <Divider style={{ margin: '8px 0 16px' }} />
        <ChampResponsabilite form={form} sujet={t('victimeModal.sujetResponsabilite')} hauteur={40} />

        {/* estAdversaire est déterminé en amont (bouton "Ajouter une victime" vs "Ajouter un adversaire")
            → on conserve la valeur dans le form mais on ne l'expose plus à l'utilisateur. */}
        <Form.Item name="estAdversaire" valuePropName="checked" hidden>
          <Switch />
        </Form.Item>

        {/* ══════ Négociation RC - uniquement pour un adversaire déjà créé ══════ */}
        {initial?.victimeTrackingId && initial?.estAdversaire && (
          <>
            <Divider style={{ margin: '24px 0 12px' }} />
            <div style={{
              fontSize: 11, fontWeight: 600, letterSpacing: '1.4px',
              textTransform: 'uppercase', color: INK_MUTED, marginBottom: 10,
            }}>
              {t('victimeModal.positionRc')}
            </div>
            <NegociationRcPanel adversaire={initial} onUpdated={onRcUpdated} />
          </>
        )}
        <div style={{ marginTop: 8, paddingTop: 14, borderTop: `1px solid ${LINE}` }}>
          <RequiredLegend />
        </div>
      </Form>
    </CBModal>
  );
}
